// VFS の seed 層を用意する。
//  - バンドル資材（config/*.json, data/**, schemas/*.schema.json）は browser/gen/bundled-files.mjs から同期投入
//  - sevenfold 共有ランタイムの既定 JSON（_shared マスタ・placeholder 元 regalus）は /_runtime/ から遅延 fetch
// 同期 read（既存 lib）は非同期 fetch を待てないため、ゲーム系 op の前に ensureRuntimeDefaults() で常駐させる。
import { vfs } from './vfs.mjs';
import { BUNDLED } from './gen/bundled-files.mjs';

let bundledDone = false;
export function ensureSeeded() {
    if (bundledDone) return;
    for (const [p, content] of Object.entries(BUNDLED)) vfs.seedFile(p, content);
    bundledDone = true;
}

// _runtime 既定 JSON（_shared マスタと placeholder 用 regalus 一式）。treasures は placeholder では使わない。
const RUNTIME_SHARED = [
    // 取り込み後のブラウザ内 treasures 生成・整合検証で読む共有マスタも常駐させる。
    'enemies.json', 'items.json', 'equipment.json', 'loot-tables.json', 'shop-master.json',
    'audio.json', 'exchange-shop.json', 'game-info.json', 'skills.json', 'characters.json',
    'i18n/locales/ja.json', 'i18n/locales/en.json',
];
const RUNTIME_REGALUS = [
    'meta.json', 'terrain.json', 'objects.json', 'npcs.json', 'enemies.json',
    'events.json', 'environment.json', 'pets.json', 'shops.json',
];

let runtimePromise = null;
export function ensureRuntimeDefaults(runtimeBase = '/_runtime') {
    if (runtimePromise) return runtimePromise;
    runtimePromise = (async () => {
        const fetchText = async (rel) => {
            // preview-sw は同じファイルを no-cache で読むため、ここも合わせる（force-cache だと
            // エンジン更新後も旧マスタで②を実行し、プレビュー側の他ファイルとリビジョンが混在する）
            const res = await fetch(`${runtimeBase}/${rel}`, { cache: 'no-cache' });
            if (!res.ok) throw new Error(`_runtime 既定の取得に失敗: ${rel} (HTTP ${res.status})`);
            return res.text();
        };
        const tasks = [];
        for (const f of RUNTIME_SHARED) {
            tasks.push(fetchText(`maps/_shared/${f}`).then((t) => vfs.seedFile(`/vfs/_runtime_defaults/maps/_shared/${f}`, t)));
        }
        for (const f of RUNTIME_REGALUS) {
            tasks.push(fetchText(`maps/regalus/${f}`).then((t) => vfs.seedFile(`/vfs/_runtime_defaults/maps/regalus/${f}`, t)));
        }
        await Promise.all(tasks);
    })().catch((e) => {
        runtimePromise = null; // 次回リトライ可能に
        throw e;
    });
    return runtimePromise;
}

// 開始ワールドの土台（マイワールド等）を必要時に取得する。ファイル構成は regalus と同一だが、
// 将来の欠けに備えて 404 はスキップ（複製側は seed 済みディレクトリの readdir を使うため安全）
const runtimeMapPromises = new Map();
export function ensureRuntimeMap(mapId, runtimeBase = '/_runtime') {
    if (!/^[a-z][a-z0-9_]*$/.test(mapId)) return Promise.reject(new Error(`不正な mapId: ${mapId}`));
    if (!runtimeMapPromises.has(mapId)) {
        runtimeMapPromises.set(mapId, (async () => {
            const tasks = RUNTIME_REGALUS.map(async (f) => {
                const res = await fetch(`${runtimeBase}/maps/${mapId}/${f}`, { cache: 'no-cache' });
                if (!res.ok) return; // 404 等はスキップ
                vfs.seedFile(`/vfs/_runtime_defaults/maps/${mapId}/${f}`, await res.text());
            });
            await Promise.all(tasks);
        })().catch((e) => {
            runtimeMapPromises.delete(mapId); // 次回リトライ可能に
            throw e;
        }));
    }
    return runtimeMapPromises.get(mapId);
}

export { RUNTIME_SHARED, RUNTIME_REGALUS };
